/**
 * Figured bass: the shorthand written under a bass line.
 *
 * A figure is not a chord and not a pitch. It is an interval above whatever note the bass
 * happens to be playing -- a `6` means "the sixth above this bass", and which note that is
 * depends entirely on the key and the bass. That is the whole reason it is modelled apart
 * from harmony: a chord symbol names an absolute chord and moves when the music is
 * transposed, and a figure names a distance and does not.
 *
 * ## What a figure holds
 *
 * A numeral, an accidental before it, and a mark after it. `♯6` raises the sixth; `6♯` and
 * `6+` raise the third of that sixth-chord -- the cross through the numeral and the
 * accidental after it are the same instruction, written differently by different engravers,
 * and both are kept as typed rather than folded together, because a player reading an urtext
 * expects to see the sign the editor printed. An accidental on its own, with no numeral, is
 * the ordinary way to alter the third, so a figure may have no number at all.
 *
 * ## The stack
 *
 * Figures are stacked, highest on top: a six-four is a `6` over a `4`. They are held in that
 * order, top first, because that is the order they are read, typed and printed in.
 *
 * ## Where it stands
 *
 * `FiguredBass.onset` is ticks from the start of its own bar, exactly as a chord symbol's
 * is, and for the same reason: a figure stands at a moment, and the bass note under it may
 * be longer than the figure or shorter. A held bass with three figures changing under it is
 * the ordinary case of continuo, not an exception.
 */

import { createId } from "./ids";
import type { FigureId } from "./ids";

/** What may be written before a numeral, and after it. */
export const FIGURE_MARKS = ["#", "b", "n", "##", "bb", "+", "\\"] as const;

export type FigureMark = (typeof FIGURE_MARKS)[number];

/** How each mark is named in MusicXML, which is the only place the name is needed. */
const MARK_NAMES: Readonly<Record<FigureMark, string>> = {
  "#": "sharp",
  b: "flat",
  n: "natural",
  "##": "double-sharp",
  bb: "flat-flat",
  "+": "cross",
  "\\": "backslash",
};

/** The mark a MusicXML name means, for reading a file back. */
const MARK_FOR: ReadonlyMap<string, FigureMark> = new Map(
  FIGURE_MARKS.map((mark) => [MARK_NAMES[mark], mark] as const),
);

export function markName(mark: FigureMark): string {
  return MARK_NAMES[mark];
}

export function markFor(name: string): FigureMark | null {
  return MARK_FOR.get(name) ?? null;
}

/** One numeral of a stack, with whatever is written around it. */
export interface Figure {
  /** An accidental before the numeral: the `♯` of `♯6`. */
  readonly prefix: FigureMark | null;
  /** The interval above the bass, or `null` when only an accidental is written. */
  readonly number: number | null;
  /** An accidental or a stroke after the numeral: the `+` of `6+`. */
  readonly suffix: FigureMark | null;
  /** Whether a line is drawn on from this figure, meaning "the same until it stops". */
  readonly extend: boolean;
}

/** One stack of figures, standing at a moment of the bar that holds it. */
export interface FiguredBass {
  readonly id: FigureId;
  /** Ticks from the start of its own bar. */
  readonly onset: number;
  /** The numerals, top of the stack first. */
  readonly figures: readonly Figure[];
  /** Whether the whole stack is bracketed, which is how an editor marks their own guess. */
  readonly parenthesized: boolean;
}

/** The highest figure anyone writes: beyond a thirteenth there is no interval left to name. */
const HIGHEST = 13;

/** What a typed accidental may look like, so that a copied figure can be typed back in. */
function normalise(text: string): string {
  return text.replace(/♯/g, "#").replace(/♭/g, "b").replace(/♮/g, "n");
}

/** One figure as it is written: `#6`, `4+`, `7_`. */
export function figureText(figure: Figure): string {
  const number = figure.number === null ? "" : String(figure.number);
  return `${figure.prefix ?? ""}${number}${figure.suffix ?? ""}${figure.extend ? "_" : ""}`;
}

/** A whole stack as it is written and as it may be typed back in. */
export function figuresText(bass: FiguredBass): string {
  const stack = bass.figures.map(figureText).join("/");
  return bass.parenthesized ? `(${stack})` : stack;
}

/**
 * Read the accidental at the head of a token, and what is left of it.
 *
 * Only accidentals may stand before a numeral -- a cross or a stroke is drawn through it or
 * after it, never in front -- so the doubles are tried before the singles and nothing else
 * is tried at all.
 */
function readPrefix(token: string): readonly [FigureMark | null, string] {
  for (const mark of ["##", "bb", "#", "b", "n"] as const) {
    if (token.startsWith(mark)) return [mark, token.slice(mark.length)];
  }
  return [null, token];
}

/** Read one numeral of the stack, or say what is wrong with it. */
function readFigure(token: string): Figure | string {
  const extend = token.endsWith("_");
  const body = extend ? token.slice(0, -1) : token;
  if (body.length === 0) {
    if (extend) return { prefix: null, number: null, suffix: null, extend: true };
    return "a figure cannot be empty";
  }

  const [prefix, afterPrefix] = readPrefix(body);
  const digits = afterPrefix.match(/^\d*/)?.[0] ?? "";
  const tail = afterPrefix.slice(digits.length);

  let suffix: FigureMark | null = null;
  if (tail.length > 0) {
    const mark = FIGURE_MARKS.find((candidate) => candidate === tail);
    if (mark === undefined) return `${tail} is not a sign a figure can carry`;
    suffix = mark;
  }

  if (digits.length === 0) {
    if (prefix === null) return `${token} is not a figure`;
    // An accidental alone alters the third, which is the commonest figure of all.
    return { prefix, number: null, suffix: null, extend };
  }

  const number = Number(digits);
  if (number < 1 || number > HIGHEST) return `${number} is not an interval a figure names`;
  return { prefix, number, suffix, extend };
}

/**
 * Read a typed stack of figures, or say what is wrong with it.
 *
 * The stack is typed top to bottom with a slash between, `6/4`, because that is how it is
 * said aloud and how every continuo player writes it in a hurry. That is also why a stroked
 * numeral is typed `5\` rather than `5/`: the slash is already spoken for, and a figure that
 * silently became two would be worse than an unfamiliar key.
 */
export function parseFigures(text: string): FiguredBass | string {
  let trimmed = normalise(text).trim();
  if (trimmed.length === 0) return "type a figure, such as 6, 6/4 or #6/5";

  const parenthesized = trimmed.startsWith("(") && trimmed.endsWith(")");
  if (parenthesized) trimmed = trimmed.slice(1, -1).trim();

  const figures: Figure[] = [];
  for (const token of trimmed.split("/")) {
    const read = readFigure(token.trim());
    if (typeof read === "string") return read;
    figures.push(read);
  }
  if (figures.length === 0) return "type a figure, such as 6, 6/4 or #6/5";

  return { id: createId("figure"), onset: 0, figures, parenthesized };
}
